import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://localhost:7001/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 seconds — Start My Day can take 10+ seconds on first sync
});

// Token is injected by the auth interceptor set up in App.tsx
// This allows access to the React auth context from outside components
let tokenGetter: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  tokenGetter = getter;
}

// Principal context — when an EA selects a principal, this ID is appended
// to all API requests as forExecutiveUserId. Set by PrincipalContext.
let _activePrincipalId: string | null = null;

/** Fired when an EA switches the executive they act for. */
export const PRINCIPAL_CHANGED_EVENT = 'cadence:principal-changed';

export function setActivePrincipalId(id: string | null) {
  if (id === _activePrincipalId) return;
  _activePrincipalId = id;
  // Per-executive answers (the AI usage on the entitlements call) follow the switch.
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(PRINCIPAL_CHANGED_EVENT));
}

apiClient.interceptors.request.use(async (config) => {
  // Auth token injection
  if (tokenGetter) {
    try {
      const token = await tokenGetter();
      config.headers.Authorization = `Bearer ${token}`;
    } catch {
      // Token expired — will get 401, handled by response interceptor
    }
  }

  // Principal context injection — append forExecutiveUserId to all requests
  // when an EA has selected a principal. Does NOT override explicit params,
  // and an explicit `forExecutiveUserId: null` means "for myself" (axios
  // leaves null params out of the URL): an EA's own setup must never touch
  // the executive they happen to be viewing.
  if (_activePrincipalId && config.params?.forExecutiveUserId === undefined) {
    config.params = { ...config.params, forExecutiveUserId: _activePrincipalId };
  }

  return config;
});

// Blob.text() where the browser has it; FileReader otherwise.
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

// Response interceptor — catches the backend's MFA-required wall.
// `RequireMfaCompleteMiddleware` returns 403 with body
// `{ error: "mfa_setup_required", message: ... }` for admin-class users
// whose token carries `mfa_pending=true` AND who hit a non-allowed
// endpoint (everything except /api/auth/mfa/*, /auth/refresh, /auth/me,
// /auth/logout). We catch the 403 globally and redirect to /mfa-setup
// so the user can't get stuck on a page that 403s without any cue —
// the redirect IS the cue.
//
// Idempotent: if we're already on /mfa-setup don't redirect again
// (would loop on the setup page's own GET /me call). The setup page's
// allowed endpoints don't return this error, but the guard is cheap.
//
// Also normalises a tenant-permission refusal (403 `{ error, code:
// "permission_denied", permission }` — an admin set this user's permission to
// No on the Permissions screen): a download (responseType 'blob') gets its
// JSON body parsed so `response.data.error` is readable like any other call,
// and `error.message` carries the server's explanation, so a page that shows
// either tells the user why rather than "Request failed with status code 403".
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (status === 403 && typeof Blob !== 'undefined' && body instanceof Blob && body.type.includes('json')) {
      try {
        error.response.data = JSON.parse(await readBlobText(body));
      } catch {
        // Not JSON after all — leave the blob as it was.
      }
    }
    const code = error?.response?.data?.error;
    if (status === 403 && code === 'mfa_setup_required') {
      if (typeof window !== 'undefined'
          && window.location.pathname !== '/mfa-setup') {
        window.location.replace('/mfa-setup');
      }
    } else if (status === 403 || status === 429) {
      await announcePlanLimit(error);
    }
    if (status === 403 && error?.response?.data?.code === 'permission_denied') {
      if (typeof error.response.data.error === 'string') error.message = error.response.data.error;
      announcePermissionDenied(error.response.data.permission);
    }
    return Promise.reject(error);
  },
);

/**
 * Fired on every permission_denied refusal, with the permission's name
 * ("CanViewReports"). PermissionsProvider re-reads /permissions/me when its
 * copy said the user had it (an admin changed it meanwhile).
 */
export const PERMISSION_DENIED_EVENT = 'cadence:permission-denied';

function announcePermissionDenied(permission: unknown) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<{ permission?: string }>(PERMISSION_DENIED_EVENT, {
    detail: { permission: typeof permission === 'string' ? permission : undefined },
  }));
}

// ---------------------------------------------------------------------------
// Plan limits. The feature gate answers 403 `{ error, code:
// "plan_upgrade_required", entitlement, requiredPlan }` for a feature the
// plan doesn't include, and the AI allowance answers 429 `{ error, code:
// "ai_allowance_exceeded", resetsAt }` at the hard stop. Both are announced
// as one window event that the single mounted <UpgradePrompt /> turns into a
// dialog. The rejection still reaches the caller untouched, so per-screen
// handling (Admin's inline SSO message, a page's own toast) keeps working.
// ---------------------------------------------------------------------------

export const PLAN_LIMIT_EVENT = 'cadence:plan-limit';

export type PlanLimitCode = 'plan_upgrade_required' | 'ai_allowance_exceeded' | 'transcript_limit_reached';

export interface PlanLimitDetail {
  code: PlanLimitCode;
  /** The server's own sentence, e.g. "This needs the Starter plan or above." */
  message: string;
  /** PascalCase entitlement name from the 403 body, e.g. "StrategicDrivers". */
  entitlement?: string;
  requiredPlan?: string;
  /** When the AI allowance resets (429 only). */
  resetsAt?: string;
  /**
   * True for a read the page made on its own (a GET that isn't a download).
   * Those don't open a dialog: the route guard and the hidden widgets already
   * explain a locked feature, and a background fetch shouldn't interrupt.
   */
  background: boolean;
}

// A download (responseType "blob") delivers its error body as a Blob too.
async function readBody(data: unknown): Promise<any> {
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try { return JSON.parse(await data.text()); } catch { return null; }
  }
  return data;
}

async function announcePlanLimit(error: any): Promise<void> {
  if (typeof window === 'undefined') return;
  const body = await readBody(error?.response?.data);
  if (!body || typeof body !== 'object') return;
  const code: string | undefined = body.code ?? body.error;
  const status = error?.response?.status;
  const isUpgrade = status === 403 && code === 'plan_upgrade_required';
  // The monthly transcript limit is an allowance too: same dialog, its own sentence.
  const isAllowance = status === 429 && (code === 'ai_allowance_exceeded' || code === 'transcript_limit_reached');
  if (!isUpgrade && !isAllowance) return;

  const config = error?.config ?? {};
  const method = String(config.method ?? 'get').toLowerCase();
  const detail: PlanLimitDetail = {
    code: isUpgrade ? 'plan_upgrade_required'
      : code === 'transcript_limit_reached' ? 'transcript_limit_reached' : 'ai_allowance_exceeded',
    message: typeof body.error === 'string' && body.error !== code
      ? body.error
      : (typeof body.message === 'string' ? body.message : ''),
    entitlement: typeof body.entitlement === 'string' ? body.entitlement : undefined,
    requiredPlan: typeof body.requiredPlan === 'string' ? body.requiredPlan : undefined,
    resetsAt: typeof body.resetsAt === 'string' ? body.resetsAt : undefined,
    // AI requests are always something the user asked for.
    background: isUpgrade && method === 'get' && config.responseType !== 'blob',
  };
  window.dispatchEvent(new CustomEvent<PlanLimitDetail>(PLAN_LIMIT_EVENT, { detail }));
}

export default apiClient;
